import type { MicSwapEvent, RecordingAdapter } from '@clarix/meeting-recording';
import type { PresignedUrlItem, UploadPartInfo } from '@clarix/shared-types';
import { quickMeetingApi } from './api';
import { ABORT_RETRY_DELAYS_MS, COMPLETE_RETRY_DELAYS_MS, DELETE_UNAVAILABLE_CODE, QM_MIC } from './constants';
import { errorCode } from './format';
import type { QuickMeetingUpload, RecordingSummary } from './types';

export type CompleteOutcome =
  | { kind: 'accepted'; summary: RecordingSummary }
  /** The server settled the row itself (too large, gone, discarded): nothing left to retry. */
  | { kind: 'final'; code: string }
  /** The server lacks parts this device sent (all of them when `parts` is empty): re-upload, keep everything. */
  | { kind: 'missing'; parts: number[] }
  /** The server ended the take with no audio although this device sent parts: keep them. */
  | { kind: 'lost'; summary: RecordingSummary }
  /** S3 lost the multipart upload itself: re-open it and send every local part again. */
  | { kind: 'gone' };

const sleep = (ms: number) => new Promise<void>((r) => window.setTimeout(r, ms));

/** The server can't match the parts it holds; terminal only when no parts were sent. */
export const NO_AUDIO_CODE = 'RECORDING_PARTS_MISMATCH';

/** Codes that a retry can never change. With `parts`, a mismatch means re-upload them (`missing`). */
export function isFinal(status: number, code: string, sentParts: boolean): boolean {
  if (status === 401 || status === 403 || status === 404 || status === 409) return true;
  return status === 422 && (code === 'RECORDING_TOO_LARGE' || (code === NO_AUDIO_CODE && !sentParts));
}

export type AbortOutcome = { ok: true } | { ok: false; code: string };

/** `POST …/abort` with back-off; ok once the row is discarded or already gone. */
export async function abortWithRetry(slug: string, recordingId: string): Promise<AbortOutcome> {
  let code = '';
  for (const delay of ABORT_RETRY_DELAYS_MS) {
    if (delay) await sleep(delay);
    try {
      await quickMeetingApi.abort(slug, recordingId);
      return { ok: true };
    } catch (err) {
      const e = errorCode(err);
      if (e.status === 404) return { ok: true };
      code = e.code;
      // S3 refused the delete; retrying now won't change that.
      if (code === DELETE_UNAVAILABLE_CODE) break;
    }
  }
  return { ok: false, code };
}

/** Trimmed to calendar-api's limits: a swap log must never be the reason a save fails validation. */
function boundedSwaps(swaps: readonly MicSwapEvent[]): MicSwapEvent[] {
  const label = (s: string | null) => (s == null ? null : s.slice(0, QM_MIC.SWAP_LABEL_MAX));
  return swaps.slice(0, QM_MIC.MAX_SWAPS).map((s) => ({ at: s.at, from: label(s.from), to: label(s.to), reason: s.reason }));
}

/** The server's `{ missing }` part numbers; empty means "assume all". */
function missingParts(details: unknown): number[] {
  const missing = (details as { missing?: unknown } | undefined)?.missing;
  return Array.isArray(missing) ? missing.filter((n): n is number => typeof n === 'number') : [];
}

const uploadGone = (details: unknown) => (details as { upload_gone?: unknown } | undefined)?.upload_gone === true;

/** A stop the server settled as empty (`no_speech`, 0 s). */
export const isEmptyStop = (r: RecordingSummary) => r.status === 'no_speech' && r.duration_seconds === 0;

/**
 * `POST …/complete`, retried. Every attempt sends this device's `parts`; only a device with none
 * leaves them out, so the server's ListParts decides. While the device holds audio the server
 * lacks, it never completes without it. Throws once every attempt failed for a retryable reason.
 */
export async function completeWithFallback(
  slug: string,
  recordingId: string,
  parts: UploadPartInfo[],
  recordedSeconds: number | undefined,
  inputSwaps?: readonly MicSwapEvent[],
): Promise<CompleteOutcome> {
  let lastError: unknown = new Error('complete failed');
  const swaps = inputSwaps ? { input_swaps: boundedSwaps(inputSwaps) } : {};
  const withParts = parts.length > 0;
  for (const delay of COMPLETE_RETRY_DELAYS_MS) {
    if (delay) await sleep(delay);
    try {
      const summary = await quickMeetingApi.complete(slug, recordingId, {
        ...(withParts ? { parts } : {}),
        ...(recordedSeconds != null ? { recorded_seconds: recordedSeconds } : {}),
        ...swaps,
      });
      return withParts && isEmptyStop(summary) ? { kind: 'lost', summary } : { kind: 'accepted', summary };
    } catch (err) {
      lastError = err;
      const { status, code, details } = errorCode(err);
      if (status === 422 && code === NO_AUDIO_CODE && uploadGone(details)) {
        // Nothing to send again from a device without parts: there is no audio anywhere.
        return withParts ? { kind: 'gone' } : { kind: 'final', code };
      }
      if (withParts && status === 422 && code === NO_AUDIO_CODE) return { kind: 'missing', parts: missingParts(details) };
      if (isFinal(status, code, withParts)) return { kind: 'final', code };
    }
  }
  throw lastError;
}

export type ReopenOutcome =
  /** A new (or already current) upload: every local part goes up again under it. */
  | { kind: 'reset'; uploadId: string }
  /** The upload the client holds is still open: retry as usual. */
  | { kind: 'open' }
  /** The audio is already in storage: complete without parts. */
  | { kind: 'stored' }
  | { kind: 'final'; code: string };

/** `POST …/reupload` (§18.3 v6.6.1). Throws for a retryable failure (the network, a 5xx). */
export async function reopenUpload(slug: string, recordingId: string, uploadId: string): Promise<ReopenOutcome> {
  try {
    const res = await quickMeetingApi.reupload(slug, recordingId, { upload_id: uploadId });
    return res.reset ? { kind: 'reset', uploadId: res.upload.upload_id } : { kind: 'open' };
  } catch (err) {
    const { status, code } = errorCode(err);
    if (status === 409 && code === 'RECORDING_UPLOAD_COMPLETE') return { kind: 'stored' };
    if (isFinal(status, code, false)) return { kind: 'final', code };
    throw err;
  }
}

export interface CalendarRecordingAdapter extends RecordingAdapter {
  /** Whether the engine reached `adapter.start`, i.e. it owns the server row. */
  readonly started: boolean;
  /** The swap log the engine handed to `complete`, kept for the shell's Retry. */
  readonly inputSwaps: readonly MicSwapEvent[] | undefined;
}

interface AdapterOptions {
  slug: string;
  recordingId: string;
  upload: QuickMeetingUpload;
  /** The shell's pause-excluded counter; the engine's own duration is wall clock. */
  recordedSeconds: () => number;
  onComplete: (outcome: CompleteOutcome) => void;
  /** Every attempt failed for a retryable reason (the network, a 5xx). */
  onCompleteFailed: (error: unknown) => void;
}

/** The engine's `RecordingAdapter`, bound to one quick-meeting recording (API-CONTRACT §18.4). */
export function createCalendarRecordingAdapter(opts: AdapterOptions): CalendarRecordingAdapter {
  const { slug, recordingId, upload } = opts;
  let started = false;
  let inputSwaps: readonly MicSwapEvent[] | undefined;
  // The fallback recorder's prefetch can race its direct fetch for one part number; only the
  // latest request may receive the URL, or two chunks would be uploaded as that part.
  const inFlight = new Map<number, (result: { presigned_urls: PresignedUrlItem[] }) => void>();

  return {
    get started() {
      return started;
    },
    get inputSwaps() {
      return inputSwaps;
    },

    async start() {
      started = true;
      return {
        upload_id: upload.upload_id,
        object_key: upload.object_key,
        part_size_bytes: upload.part_size_bytes,
        presigned_urls: [...upload.presigned_urls],
        max_duration_minutes: upload.max_duration_minutes,
      };
    },

    moreUrls({ upload_id, next_part_number }) {
      return new Promise((resolve, reject) => {
        inFlight.get(next_part_number)?.({ presigned_urls: [] });
        let settled = false;
        const settle = (result: { presigned_urls: PresignedUrlItem[] }) => {
          if (settled) return;
          settled = true;
          resolve(result);
        };
        inFlight.set(next_part_number, settle);
        quickMeetingApi
          .uploadUrls(slug, recordingId, { upload_id, next_part_number })
          .then(settle, (err: unknown) => {
            if (settled) return;
            settled = true;
            reject(err);
          })
          .finally(() => {
            if (inFlight.get(next_part_number) === settle) inFlight.delete(next_part_number);
          });
      });
    },

    // The engine awaits this and clears IndexedDB only when `accepted`. It never throws: a rejection
    // would raise the engine's Projects-worded toast; the shell shows its own Retry.
    async complete({ parts }, meta) {
      inputSwaps = meta?.input_swaps;
      try {
        const outcome = await completeWithFallback(slug, recordingId, parts, opts.recordedSeconds(), inputSwaps);
        opts.onComplete(outcome);
        const accepted = outcome.kind === 'accepted';
        const status = accepted ? outcome.summary.status : 'failed';
        return { job_id: null, recording_status: status, mom_generated: false, accepted };
      } catch (err) {
        opts.onCompleteFailed(err);
        return { job_id: null, recording_status: 'upload_failed', mom_generated: false, accepted: false };
      }
    },

    // A stop that could not finish (not for want of a network): the row shows "Couldn't save".
    async markUploadFailed() {
      try {
        await quickMeetingApi.heartbeat(slug, recordingId, {
          recorded_seconds: opts.recordedSeconds(),
          paused: false,
          upload_failed: true,
        });
      } catch {
        /* without a heartbeat the row reads as paused, which keeps it too */
      }
    },

    // Only the engine's public abort() reaches this; discard is always an explicit `POST …/abort`.
    async abort() {
      return { message: 'ignored' };
    },
  };
}
